import { useToast } from "@ligo/layout-design-core";
import { ReactComponent as PasswordIcon } from "assets/icons/key.svg";
import { ReactComponent as AvatarIcon } from "assets/imgs/profile.svg";
import { ReactComponent as AdministrativeIcon } from "assets/sidebar/administrative.svg";
import { ReactComponent as AttendanceIcon } from "assets/sidebar/attendance.svg";
import { ReactComponent as AuditIcon } from "assets/sidebar/audit.svg";
import { ReactComponent as CompanyIcon } from "assets/sidebar/company.svg";
import { ReactComponent as DashboardIcon } from "assets/sidebar/dashboard.svg";
import { ReactComponent as ImportIcon } from "assets/sidebar/import.svg";
import { ReactComponent as NoteIcon } from "assets/sidebar/note.svg";
import { ReactComponent as PauseIcon } from "assets/sidebar/pause.svg";
import { ReactComponent as ReportIcon } from "assets/sidebar/report.svg";
import { ReactComponent as RegisterIcon } from "assets/sidebar/setting.svg";
import { ReactComponent as SignOutIcon } from "assets/sidebar/signOut.svg";
import { environmentIsDevelopment, ls, menuKey } from "consts";
import {
  useAttendList,
  useAuth,
  useInterval,
  useProfile,
  useTelemetry,
} from "providers";
import { ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { formatAgentName } from "utils";
import { ProfileAvatar } from "./styles";

type NavItemType = {
  icon: ReactNode;
  label?: string;
  name: string;
  path?: string;
  hasPermission?: boolean;
  subItems?: {
    label: string;
    path?: string;
    icon?: ReactNode;
    hasPermission?: boolean;
    action?: () => void;
  }[];
};

const initialCollapse = JSON.parse(ls.getItem(menuKey) ?? "false");

export default function HeaderViewModel() {
  const [isCollapsed, setIsCollapsed] = useState(initialCollapse);
  const { hasAttendances } = useAttendList();
  const { inPause, handleOpenModal } = useInterval();
  const profile = useProfile();
  const { user, selectedProject } = profile;
  const { isSagoCore, isSagoDS, isDevelopmentRole } = profile;
  const { goOut, onOpenProjectSelect, onOpenNotepad } = profile;
  const { t } = useTranslation("components");
  const { changePassword } = useAuth();
  const { onOpenTelemetry } = useTelemetry();
  const { warning } = useToast();
  const navigate = useNavigate();

  const hasUser = useMemo(() => !!user, [user]);

  const goToAttendance = useCallback(() => {
    warning({ description: t("alerts.finishYourAttendances") });
    navigate("./attendance");
  }, [warning, t, navigate]);

  const handleSignOut = useCallback(async () => {
    if (hasAttendances && !environmentIsDevelopment) goToAttendance();
    else await goOut();
  }, [hasAttendances, goToAttendance, goOut]);

  const profileItems = useMemo(
    () =>
      new Array<NavItemType>(
        {
          icon: <ProfileAvatar icon={<AvatarIcon />} $inPause={inPause} />,
          label: t("sideBar.profile"),
          name: formatAgentName(user?.nmUser) || "...",
          subItems: [
            {
              label: t("pause"),
              icon: <PauseIcon />,
              hasPermission: user?.hasAccessAttend && !isSagoCore && !isSagoDS,
              action: handleOpenModal,
            },
            {
              label: t("notepad"),
              icon: <NoteIcon />,
              action: onOpenNotepad,
            },
            {
              label: t("changePassword"),
              icon: <PasswordIcon />,
              hasPermission: !user?.mustBlockChangePassword,
              action: changePassword,
            },
            {
              label: t("exit"),
              icon: <SignOutIcon />,
              action: handleSignOut,
            },
          ].filter(({ hasPermission }) => hasPermission ?? true),
        },
        {
          icon: <CompanyIcon />,
          label: t("sideBar.company"),
          name: "Ligo",
          path: "company",
          hasPermission: environmentIsDevelopment && false, // ToDo: Remover quando página finalizada
        },
      ).filter(({ hasPermission }) => hasPermission ?? true),
    [
      user,
      isSagoCore,
      isSagoDS,
      inPause,
      handleOpenModal,
      onOpenNotepad,
      changePassword,
      handleSignOut,
      t,
    ],
  );

  const navItems: NavItemType[] = useMemo(() => {
    switch (true) {
      case isSagoCore:
        return new Array<NavItemType>(
          {
            icon: <DashboardIcon />,
            name: t("sideBar.dashboard.title"),
            hasPermission: user?.hasAccessDashboard,
            subItems: [
              {
                label: t("sideBar.dashboard.queue"),
                path: "./dashboard",
              },
            ],
          },
          {
            icon: <ReportIcon />,
            name: t("sideBar.report.title"),
            hasPermission: user?.hasAccessReport,
            subItems: [
              {
                label: t("sideBar.report.generator"),
                path: "./report/generator",
              },
            ],
          },
          {
            icon: <RegisterIcon />,
            name: t("sideBar.registration.title"),
            hasPermission: user?.hasAccessRegister,
            subItems: [
              {
                label: t("sideBar.registration.user"),
                path: "./registration",
              },
            ],
          },
          {
            icon: <AdministrativeIcon />,
            name: t("sideBar.administrative.title"),
            hasPermission: user?.hasAccessAdmin,
            subItems: [
              {
                label: t("sideBar.administrative.project"),
                path: "./administrative",
              },
            ],
          },
          {
            icon: <ImportIcon />,
            name: t("sideBar.import"),
            path: "import",
            hasPermission: environmentIsDevelopment,
          },
        ).filter(({ hasPermission }) => hasPermission ?? true);
      case isSagoDS:
        return new Array<NavItemType>(
          {
            icon: <DashboardIcon />,
            name: t("sideBar.dashboard.title"),
            hasPermission: user?.hasAccessDashboard,
            subItems: [
              {
                label: t("sideBar.dashboard.metricsQueue"),
                path: "./dashboard/metricsQueue",
              },
              {
                label: t("sideBar.dashboard.metricsTrunk"),
                path: "./dashboard/metricsTrunk",
                hasPermission: environmentIsDevelopment,
              },
              {
                label: `${t("sideBar.dashboard.metricsTrunk")} - Tabs`,
                path: "./dashboard/metricsTrunkTab",
                hasPermission: environmentIsDevelopment,
              },
            ].filter(({ hasPermission }) => hasPermission ?? true),
          },
          {
            icon: <ReportIcon />,
            name: t("sideBar.report.title"),
            hasPermission: user?.hasAccessReport && environmentIsDevelopment,
            subItems: [
              {
                label: t("sideBar.report.generator"),
                path: "./report/generator",
              },
            ],
          },
          {
            icon: <RegisterIcon />,
            name: t("sideBar.registration.title"),
            hasPermission: user?.hasAccessRegister && environmentIsDevelopment,
            subItems: [
              {
                label: t("sideBar.registration.campaign"),
                path: "./registration/campaign",
              },
              {
                label: t("sideBar.registration.ddd"),
                path: "./registration/ddd",
              },
              {
                label: t("sideBar.registration.attendGroup"),
                path: "./registration/attendGroup",
              },
              {
                label: t("sideBar.registration.campaignAttendGroup"),
                path: "./registration/campaignAttendGroup",
              },
              {
                label: t("sideBar.registration.userAttendGroup"),
                path: "./registration/userAttendGroup",
              },
            ],
          },
        ).filter(({ hasPermission }) => hasPermission ?? true);
      default:
        return new Array<NavItemType>(
          {
            icon: <AttendanceIcon />,
            name: t("sideBar.attendance"),
            path: "attendance",
            hasPermission: user?.hasAccessAttend,
          },
          {
            icon: <DashboardIcon />,
            name: t("sideBar.dashboard.title"),
            hasPermission: user?.hasAccessDashboard,
            subItems: [
              {
                label: t("sideBar.dashboard.queue"),
                path: "./dashboard",
              },
            ],
          },
          {
            icon: <AuditIcon />,
            name: t("sideBar.audit.title"),
            hasPermission: user?.hasAccessAudit && user?.hasOrderModule,
            subItems: [
              {
                label: t("sideBar.audit.order"),
                path: "./audit",
                hasPermission: user?.hasOrderModule,
              },
            ].filter(({ hasPermission }) => hasPermission ?? true),
          },
          {
            icon: <ReportIcon />,
            name: t("sideBar.report.title"),
            hasPermission: user?.hasAccessReport,
            subItems: [
              {
                label: t("sideBar.report.history"),
                path: "./report",
              },
              {
                label: t("sideBar.report.generator"),
                path: "./report/generator",
              },
            ],
          },
          {
            icon: <RegisterIcon />,
            name: t("sideBar.registration.title"),
            hasPermission: user?.hasAccessRegister,
            subItems: [
              {
                label: t("sideBar.registration.userChannel"),
                path: "./registration/userChannel",
              },
              {
                label: t("sideBar.registration.hsm"),
                path: "./registration/hsm",
              },
              {
                label: t("sideBar.registration.channel"),
                path: "./registration/channel",
              },
              {
                label: t("sideBar.registration.faq"),
                path: "./registration/faq",
              },
              {
                label: t("sideBar.registration.product"),
                path: "./registration/product",
              },
              {
                label: t("sideBar.registration.ticket"),
                path: "./registration/ticket",
                hasPermission: user?.hasTicketModule,
              },
              {
                label: t("sideBar.registration.attendance"),
                path: "./registration/attendance",
              },
              {
                label: t("sideBar.registration.customTable"),
                path: "./registration/customTable",
                hasPermission: isDevelopmentRole,
              },
            ].filter(({ hasPermission }) => hasPermission ?? true),
          },
          {
            icon: <AdministrativeIcon />,
            name: t("sideBar.administrative.title"),
            hasPermission: user?.hasAccessAdmin,
            subItems: [
              {
                label: t("sideBar.administrative.permission"),
                path: "./administrative/permission",
              },
              {
                label: t("sideBar.administrative.status"),
                path: "./administrative/status",
              },
              {
                label: t("sideBar.administrative.form"),
                path: "./administrative/form",
              },
              {
                label: t("sideBar.administrative.pause"),
                path: "./administrative/pause",
              },
              {
                label: t("sideBar.administrative.campaignType"),
                path: "./administrative/campaignType",
              },
            ],
          },
        ).filter(({ hasPermission }) => hasPermission ?? true);
    }
  }, [user, isSagoCore, isSagoDS, isDevelopmentRole, t]);

  const handleToggleCollapse = useCallback(() => {
    setIsCollapsed(!isCollapsed);
    ls.setItem(menuKey, JSON.stringify(!isCollapsed));
  }, [isCollapsed]);

  return {
    isCollapsed,
    selectedProject,
    hasUser,
    profileItems,
    navItems,
    t,
    onOpenProjectSelect,
    handleToggleCollapse,
    onOpenTelemetry,
  };
}
